import { useEffect, useMemo } from 'react'
import useDefaultAirport from './useDefaultAirport'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import { fetchFlightFareFamilies } from 'actions/FlightActions'
import getObjectKey from 'lib/object/getObjectKey'
import useFlightsSearchInsuranceQuote from './Insurance/useFlightsSearchInsuranceQuote'
import { cloneWithInsurance, getCheapestEconomyFareFamilyWithBaggage } from 'lib/flights/flightUtils'
import { getFlightSearchInsuranceQuoteKey, isStandaloneMaldivesCharterFlight } from 'lib/insurance/insuranceFlights'
import useExperiment from './Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import isBookingProtectionEnabled from 'checkout/selectors/request/isBookingProtectionEnabled'
import { FlightViewTypes } from 'constants/flight'
import { shouldFetchInsuranceQuotes } from 'lib/insurance/insuranceUtils'
import { getFeederOriginAirportCode } from 'checkout/selectors/view/flights'
import { selectShouldUseInsuranceMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { canRequestFlightLuxPlusPricing } from 'lib/flights/flightLuxPlusPricing'
import { getIsEnabledFlightsBundledLuxPlusPricing, getIsEnabledFlightsLuxPlusPricing } from 'luxPlus/selectors/featureToggle'

interface Props {
  searchId: string
  sessionId: string
  destinationAirportCode: string
  destinationCountry?: string
  departingDate?: string
  arrivalDate?: string
  occupancies?: App.Occupants
  journeyId?: string
  originAirportCode?: string
  departingJourneyId?: string
  provider?: App.FlightProviders
  isStandalone?: boolean
  isReturnType?: boolean
  isCombinedUpsell?: boolean
  departingFareFamily?: App.FlightFareFamily
  flightItem?: App.Checkout.FlightItem
  cachedFareFamilies?: App.FlightsState['fareFamiliesByFlight'][0]
  skipInsuranceAttachment?: boolean
}

const EmptyFareFamilyState: App.FlightsState['fareFamiliesByFlight'][0] = {
  families: [],
  familiesPerLeg: [],
  error: undefined,
  fetching: true,
}

function useFlightFareFamilies({
  searchId,
  sessionId,
  occupancies,
  destinationAirportCode,
  destinationCountry,
  journeyId,
  originAirportCode,
  departingJourneyId,
  provider,
  isStandalone,
  departingDate,
  arrivalDate,
  isReturnType,
  isCombinedUpsell,
  departingFareFamily,
  flightItem,
  cachedFareFamilies,
  skipInsuranceAttachment = false,
}: Props) {
  const departingFareFamilyId = departingFareFamily?.id
  const bookingProtectionEnabled = useAppSelector(state => isBookingProtectionEnabled(state))
  const useMemberInsurancePrice = useAppSelector(selectShouldUseInsuranceMemberPrice)
  const headlessMode = useAppSelector(state => state.system.headlessMode)

  const defaultAirport = useDefaultAirport()
  const origin = originAirportCode ?? defaultAirport?.code
  const feederOriginAirportCode = useAppSelector(getFeederOriginAirportCode)
  const isBundledFlow = !isStandalone && (flightItem?.bundledItemIds?.length ?? 0) > 0
  // Same real-membership-or-spoof-preview eligibility check that gates the flight fare's own
  // LuxPlus price (see FareFamilyFlightTileV2.tsx) - the embedded Protect insurance must agree
  // with it exactly, or a non-member/non-preview-eligible viewer could see a member discount on
  // the insurance while seeing none on the flight fare itself, or vice versa.
  // Must match fetchFlightFareFamilies' own enabled computation exactly (FlightActions.ts) - this
  // value feeds the cache key below used to READ state.flights.fareFamiliesByFlight, while that
  // thunk independently recomputes the same flag to decide the key it WRITES the response under.
  // Any divergence between the two produces a read key that never matches the write key, leaving
  // the hook stuck on its initial fetching:true state even after the request has completed.
  const isFlightLuxPlusPricingEligible = useAppSelector(state => canRequestFlightLuxPlusPricing(state, {
    enabled: (!!isStandalone && getIsEnabledFlightsLuxPlusPricing(state)) || (isBundledFlow && getIsEnabledFlightsBundledLuxPlusPricing(state)),
  }))

  const dispatch = useAppDispatch()
  const filterKey = getObjectKey({
    origin,
    destination: destinationAirportCode,
    fareId: journeyId,
    searchId,
    departingFareId: departingJourneyId,
    departingUpsellOptionId: flightItem?.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ? undefined : departingFareFamilyId,
    feederOriginAirportCode,
    // Standalone and checkout/bundled fetches for the same journey+fare price fare families
    // differently (cart-relative vs standalone) - without this, reselecting the same fare after
    // a checkout round-trip can reuse a stale cart-relative ($0) response from the other context.
    isStandalone,
    requestLuxPlusPricing: isFlightLuxPlusPricingEligible,
  })

  useEffect(() => {
    if (!cachedFareFamilies && destinationAirportCode && journeyId && occupancies && origin) {
      dispatch(fetchFlightFareFamilies({
        origin,
        destination: destinationAirportCode,
        journeyId,
        occupancies,
        searchId,
        sessionId,
        departingJourneyId,
        departingFareFamilyId: flightItem?.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN ? undefined : departingFareFamilyId,
        isStandalone,
        isBundledFlow,
        feederOriginAirportCode,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, cachedFareFamilies])

  const fareFamilyFromSelector = useAppSelector(state => state.flights.fareFamiliesByFlight[filterKey])

  const fareFamilyState = cachedFareFamilies !== undefined ? cachedFareFamilies : (fareFamilyFromSelector ?? EmptyFareFamilyState)

  const shouldHideEmbeddedInsurance = isStandaloneMaldivesCharterFlight({
    isStandalone,
    provider,
    originAirportCode: origin,
    destinationAirportCode,
  })

  const canTriggerBundledFlexibleFlightFaresExperiment = !headlessMode && !isStandalone && bookingProtectionEnabled && !!fareFamilyState && !fareFamilyState.fetching
  const { isAnyVariant: isCroBundledFlexibleFlightFaresVariant } = useExperiment(
    Experiments.croBundledFlexibleFlightFaresV2,
    { canActivate: canTriggerBundledFlexibleFlightFaresExperiment },
  )
  const isCroBundledFlexibleFlightFaresEnabled = canTriggerBundledFlexibleFlightFaresExperiment && isCroBundledFlexibleFlightFaresVariant

  const { fetching: fetchingInsuranceQuote, quotes: insuranceQuotes } = useFlightsSearchInsuranceQuote(
    fareFamilyState,
    occupancies,
    destinationCountry ? [destinationCountry] : [],
    departingDate,
    arrivalDate,
    {
      disabled: skipInsuranceAttachment || !shouldFetchInsuranceQuotes({
        isStandalone,
        isCroBundledFlexibleFlightFaresEnabled,
        bookingProtectionEnabled,
        isFlightDateChange: !!cachedFareFamilies,
      }) || shouldHideEmbeddedInsurance,
    },
    isReturnType,
    isCombinedUpsell,
    departingFareFamily,
  )

  const fareFamiliesStateWithInsurance = useMemo(() => {
    if (skipInsuranceAttachment || shouldHideEmbeddedInsurance) return fareFamilyState?.families || []

    if (!fareFamilyState?.families?.length || fetchingInsuranceQuote) return []

    const { cheapestEconomyWithBaggage, pairedReturningFareFamily, journeyTotalCost } = getCheapestEconomyFareFamilyWithBaggage(fareFamilyState.families, isReturnType)
    if (!cheapestEconomyWithBaggage) return fareFamilyState.families

    return fareFamilyState.families.reduce((acc: Array<App.FlightFareFamily>, family) => {
      acc.push(family)
      if (family === cheapestEconomyWithBaggage) {
        if (isReturnType && !pairedReturningFareFamily) return acc

        const quoteKey = getFlightSearchInsuranceQuoteKey(
          departingDate,
          arrivalDate,
          journeyTotalCost + (!isReturnType && departingFareFamily?.withEmbeddedInsurance ? departingFareFamily.cost.all : 0),
          destinationCountry,
          occupancies,
        )

        const quote = insuranceQuotes[quoteKey]

        if (quote) {
          const insuranceVersion = cloneWithInsurance(family, quote, occupancies, isReturnType, undefined, useMemberInsurancePrice, isFlightLuxPlusPricingEligible)
          acc.push(insuranceVersion)

          if (pairedReturningFareFamily) {
            const pairedInsuranceVersion = cloneWithInsurance(pairedReturningFareFamily, quote, occupancies, isReturnType, undefined, useMemberInsurancePrice, isFlightLuxPlusPricingEligible)
            pairedInsuranceVersion.pairedUpsellIds = undefined
            insuranceVersion.pairedUpsellIds = [pairedInsuranceVersion.id]
            acc.push(pairedInsuranceVersion)
          }
        }
      }
      return acc
    }, [])
  }, [fareFamilyState?.families, fetchingInsuranceQuote, departingDate, arrivalDate, destinationCountry, occupancies, insuranceQuotes, isReturnType, departingFareFamily?.withEmbeddedInsurance, departingFareFamily?.cost.all, shouldHideEmbeddedInsurance, skipInsuranceAttachment, useMemberInsurancePrice, isFlightLuxPlusPricingEligible])

  const fareFamiliesStatePerLegWithInsurance = useMemo(() => {
    const familiesPerLeg = fareFamilyState?.familiesPerLeg

    if (skipInsuranceAttachment || shouldHideEmbeddedInsurance) return familiesPerLeg || []
    if (!familiesPerLeg?.length || fetchingInsuranceQuote) return []

    const result: Array<Array<App.FlightFareFamily>> = familiesPerLeg.length === 1 ? [[]] : [[], []]
    const isMixedUpsell = fareFamilyState?.isMixedUpsell ?? false
    const addedReturningFamiliesById = isMixedUpsell ? new Map<string, App.FlightFareFamily>() : null

    const departingEligible = getCheapestEconomyFareFamilyWithBaggage(familiesPerLeg[0], false)?.cheapestEconomyWithBaggage
    const linkedReturningEligible = getCheapestEconomyFareFamilyWithBaggage(
      familiesPerLeg[1]?.filter(returnFamily => departingEligible && returnFamily.pairedUpsellIds?.includes(departingEligible.id)),
      false,
    )?.cheapestEconomyWithBaggage

    for (const departingFamily of familiesPerLeg[0]) {
      const cloneDepartingFamily = { ...departingFamily }
      result[0].push(cloneDepartingFamily)

      let departingInsuranceVersion: App.FlightFareFamily | undefined

      if (cloneDepartingFamily.id === departingEligible?.id) {
        const quoteKey = getFlightSearchInsuranceQuoteKey(
          departingDate,
          arrivalDate,
          cloneDepartingFamily.cost.all,
          destinationCountry,
          occupancies,
        )

        const quote = insuranceQuotes[quoteKey]

        if (quote) {
          departingInsuranceVersion = cloneWithInsurance(cloneDepartingFamily, quote, occupancies, false, undefined, useMemberInsurancePrice, isFlightLuxPlusPricingEligible)
          result[0].push(departingInsuranceVersion)
        }
      }

      const pairedReturnFamilies = familiesPerLeg[1]?.filter(returnFamily => returnFamily.pairedUpsellIds?.includes(departingFamily.id)) ?? []
      const looseReturningEligible = getCheapestEconomyFareFamilyWithBaggage(pairedReturnFamilies, false)?.cheapestEconomyWithBaggage

      for (const returningFamily of pairedReturnFamilies) {
        const cloneReturningFamily = { ...returningFamily, pairedUpsellIds: [...returningFamily.pairedUpsellIds ?? []] }
        if (departingInsuranceVersion) {
          cloneReturningFamily.pairedUpsellIds = [...(returningFamily.pairedUpsellIds ?? []), departingInsuranceVersion.id]
        }

        // For LCC (isMixedUpsell), each returning family can be paired with multiple departing families.
        // Only push the base returning fare once; insurance variants are created per-departing-family below
        // so they are not deduplicated — they carry distinct pairedUpsellIds.
        const isFirstSeen = !addedReturningFamiliesById?.has(returningFamily.id)
        if (isFirstSeen) {
          addedReturningFamiliesById?.set(returningFamily.id, cloneReturningFamily)
          result[1].push(cloneReturningFamily)
        } else if (addedReturningFamiliesById) {
          const existing = addedReturningFamiliesById.get(returningFamily.id)
          if (existing && departingInsuranceVersion && !existing.pairedUpsellIds?.includes(departingInsuranceVersion.id)) {
            existing.pairedUpsellIds = [...(existing.pairedUpsellIds ?? []), departingInsuranceVersion.id]
          }
        }

        if (cloneReturningFamily.id === looseReturningEligible?.id) {
          const quoteKey = getFlightSearchInsuranceQuoteKey(
            departingDate,
            arrivalDate,
            cloneReturningFamily.cost.all,
            destinationCountry,
            occupancies,
          )

          const quote = insuranceQuotes[quoteKey]

          if (quote) {
            const returningInsuranceVersion = cloneWithInsurance(cloneReturningFamily, quote, occupancies, false, undefined, useMemberInsurancePrice, isFlightLuxPlusPricingEligible)
            returningInsuranceVersion.pairedUpsellIds = [cloneDepartingFamily.id]
            result[1].push(returningInsuranceVersion)
          }
        }

        if (departingInsuranceVersion && cloneReturningFamily.id === linkedReturningEligible?.id) {
          const quoteKey = getFlightSearchInsuranceQuoteKey(
            departingDate,
            arrivalDate,
            (departingEligible?.cost.all ?? 0) + (linkedReturningEligible?.cost.all ?? 0),
            destinationCountry,
            occupancies,
          )

          const quote = insuranceQuotes[quoteKey]

          if (quote) {
            const returningInsuranceVersion = cloneWithInsurance(cloneReturningFamily, quote, occupancies, false, '2', useMemberInsurancePrice, isFlightLuxPlusPricingEligible)
            returningInsuranceVersion.pairedUpsellIds = [departingInsuranceVersion.id]

            const departingInsuranceCost = {
              adult: (departingInsuranceVersion.costWithInsurance?.adult ?? 0) - (departingInsuranceVersion.cost.adult ?? 0),
              children: (departingInsuranceVersion.costWithInsurance?.children ?? 0) - (departingInsuranceVersion.cost.children ?? 0),
              infant: (departingInsuranceVersion.costWithInsurance?.infant ?? 0) - (departingInsuranceVersion.cost.infant ?? 0),
              all: (departingInsuranceVersion.costWithInsurance?.all ?? 0) - (departingInsuranceVersion.cost.all ?? 0),
            }

            /* The departingInsuranceVersion contains quote for departing flight only, but the returningInsuranceVersion contains quote for both departing and returning flights,
            so we need this subtraction for consistently showing the correct price in the Combined Fare Family Select Modal */
            returningInsuranceVersion.costWithInsurance = {
              adult: (returningInsuranceVersion.costWithInsurance?.adult ?? 0) - departingInsuranceCost.adult,
              children: (returningInsuranceVersion.costWithInsurance?.children ?? 0) - departingInsuranceCost.children,
              infant: (returningInsuranceVersion.costWithInsurance?.infant ?? 0) - departingInsuranceCost.infant,
              all: (returningInsuranceVersion.costWithInsurance?.all ?? 0) - departingInsuranceCost.all,
            }

            const departingLuxPlusInsuranceCost = {
              adult: (departingInsuranceVersion.luxPlusCostWithInsurance?.adult ?? departingInsuranceVersion.costWithInsurance?.adult ?? 0) - departingInsuranceVersion.cost.adult,
              children: (departingInsuranceVersion.luxPlusCostWithInsurance?.children ?? departingInsuranceVersion.costWithInsurance?.children ?? 0) - departingInsuranceVersion.cost.children,
              infant: (departingInsuranceVersion.luxPlusCostWithInsurance?.infant ?? departingInsuranceVersion.costWithInsurance?.infant ?? 0) - departingInsuranceVersion.cost.infant,
              all: (departingInsuranceVersion.luxPlusCostWithInsurance?.all ?? departingInsuranceVersion.costWithInsurance?.all ?? 0) - departingInsuranceVersion.cost.all,
            }

            returningInsuranceVersion.luxPlusCostWithInsurance = {
              adult: (returningInsuranceVersion.luxPlusCostWithInsurance?.adult ?? returningInsuranceVersion.costWithInsurance?.adult ?? 0) - departingLuxPlusInsuranceCost.adult,
              children: (returningInsuranceVersion.luxPlusCostWithInsurance?.children ?? returningInsuranceVersion.costWithInsurance?.children ?? 0) - departingLuxPlusInsuranceCost.children,
              infant: (returningInsuranceVersion.luxPlusCostWithInsurance?.infant ?? returningInsuranceVersion.costWithInsurance?.infant ?? 0) - departingLuxPlusInsuranceCost.infant,
              all: (returningInsuranceVersion.luxPlusCostWithInsurance?.all ?? returningInsuranceVersion.costWithInsurance?.all ?? 0) - departingLuxPlusInsuranceCost.all,
            }

            result[1].push(returningInsuranceVersion)
          }
        }
      }
    }

    return result
  }, [
    fareFamilyState?.familiesPerLeg,
    fareFamilyState?.isMixedUpsell,
    fetchingInsuranceQuote,
    departingDate,
    arrivalDate,
    destinationCountry,
    occupancies,
    insuranceQuotes,
    shouldHideEmbeddedInsurance,
    skipInsuranceAttachment,
    useMemberInsurancePrice,
    isFlightLuxPlusPricingEligible,
  ])

  return {
    families: fareFamiliesStateWithInsurance,
    familiesPerLeg: fareFamiliesStatePerLegWithInsurance,
    fetching: !!fareFamilyState?.fetching || fetchingInsuranceQuote,
    error: fareFamilyState?.error,
    fareFamiliesState: fareFamilyState?.families,
    fareFamiliesKey: filterKey,
    insuranceQuotes,
  }
}

export default useFlightFareFamilies
